import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {NucleotideType} from 'biojs/BioConstants';
import {PolymerSubtype} from 'constants/MolbioConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {QueryResultType, useBenchlingQuery, BenchlingQueryResult} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  getLinkContainersSummary,
  ReferencedPolymerCountsByComponentLinkType,
} from 'molbio/biointelligent-links/BiointelligentLinkComponentInfoBanner';
import {
  BiointelligentLinkType,
  LINK_TYPE_TO_COMPONENT_NAME,
} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  PolymerClonedFromQuery,
  PolymerClonedFromQuery_polymer_ProteinNode_clonedFrom_ProteinNode as ClonedFromProteinNode,
  PolymerClonedFromQuery_polymer_SequenceNode_clonedFrom_SequenceNode as ClonedFromSequenceNode,
  PolymerClonedFromQueryVariables,
} from 'molbio/biointelligent-links/replace-link/__generated/PolymerClonedFromQuery';
import {ReplaceableBiointelligentLinkType} from 'molbio/biointelligent-links/replace-link/constants';
import ReplaceBiointelligentLinksModal from 'molbio/biointelligent-links/replace-link/ReplaceBiointelligentLinksModal';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Link from 'ui-library/Link';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';
import {capitalize} from 'util/NameUtil';

export const POLYMER_CLONED_FROM_QUERY = gql`
  query PolymerClonedFromQuery($id: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    polymer(id: $id) {
      ... on SequenceNode {
        id
        clonedFrom {
          ... on SequenceNode {
            id
            nucleotideType
            name
            tagSchema {
              id
            }
            partContainerCount
            transcriptionContainerCount
          }
        }
      }
      ... on ProteinNode {
        id
        clonedFrom {
          ... on ProteinNode {
            id
            name
            tagSchema {
              id
            }
            partContainerCount
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const getClonedFromPolymerIfAvailable = (
  queryResult: BenchlingQueryResult<PolymerClonedFromQuery, PolymerClonedFromQueryVariables>
): ClonedFromSequenceNode | ClonedFromProteinNode | null => {
  if (
    queryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(queryResult.data.polymer) &&
    queryResult.data.polymer.clonedFrom != null &&
    !isInaccessible(queryResult.data.polymer.clonedFrom)
  ) {
    return queryResult.data.polymer.clonedFrom;
  }
  return null;
};

const hasNoLinks = (clonedFrom: ClonedFromSequenceNode | ClonedFromProteinNode): boolean => {
  if (clonedFrom.__typename === 'SequenceNode') {
    return clonedFrom.partContainerCount === 0 && clonedFrom.transcriptionContainerCount === 0;
  }
  return clonedFrom.partContainerCount === 0;
};

interface Props {
  isRegistered: boolean;
  polymerId: string;
  tagSchemaId: string | null;
}

export default function ReplaceBiointelligentLinksInfoBanner(props: Props): JSX.Element {
  const {isRegistered, polymerId, tagSchemaId} = props;
  const queryResult = useBenchlingQuery<PolymerClonedFromQuery, PolymerClonedFromQueryVariables>(
    POLYMER_CLONED_FROM_QUERY,
    {variables: {id: polymerId}}
  );
  const maybeClonedFrom = getClonedFromPolymerIfAvailable(queryResult);
  if (
    maybeClonedFrom == null ||
    tagSchemaId !== maybeClonedFrom.tagSchema?.id ||
    hasNoLinks(maybeClonedFrom)
  ) {
    return <></>;
  }
  let linkCounts: ReferencedPolymerCountsByComponentLinkType;
  if (maybeClonedFrom.__typename === 'ProteinNode') {
    linkCounts = {
      type: PolymerSubtype.aa_sequence,
      references: {
        [BiointelligentLinkType.AMINO_ACID_PART]: maybeClonedFrom.partContainerCount,
        [BiointelligentLinkType.TRANSLATION]: 0,
      },
    };
  } else if (maybeClonedFrom.nucleotideType === NucleotideType.DNA) {
    linkCounts = {
      type: PolymerSubtype.dna_sequence,
      references: {
        [BiointelligentLinkType.DNA_PART]: maybeClonedFrom.partContainerCount,
      },
    };
  } else {
    linkCounts = {
      type: PolymerSubtype.rna_sequence,
      references: {
        [BiointelligentLinkType.RNA_PART]: maybeClonedFrom.partContainerCount,
        [BiointelligentLinkType.TRANSCRIPTION]: maybeClonedFrom.transcriptionContainerCount,
      },
    };
  }
  const usedLinkTypes = Object.keys(linkCounts.references).filter(
    (linkType: keyof typeof linkCounts.references) => linkCounts.references[linkType] > 0
  ) as Array<ReplaceableBiointelligentLinkType>;

  const onLaunchReplaceFlow = (linkType: ReplaceableBiointelligentLinkType): void => {
    runModal(ReplaceBiointelligentLinksModal, {
      existingLinkPolymerId: maybeClonedFrom.id,
      newLinkPolymerId: polymerId,
      linkType,
    });
  };

  const sharedReplaceLinkButtonProps = {
    buttonStyle: ButtonStyle.PRIMARY,
    size: ButtonSize.MINI,
    text: 'Replace link',
    disabled: !isRegistered,
    className: css(styles.replaceButton),
  };

  const message = (
    <div className={css(styles.messageWrapper)}>
      <div className={css(styles.infoText)}>
        This sequence was created from{' '}
        <Link
          onClick={() => {
            FileBrowserActions.openFile(maybeClonedFrom.id);
          }}
        >
          {maybeClonedFrom.name}
        </Link>{' '}
        which is referenced as {getLinkContainersSummary(linkCounts)}. You can replace {maybeClonedFrom.name}{' '}
        with this sequence and update links
        {isRegistered ? '' : ' after registering'}.
      </div>
      {usedLinkTypes.length < 2 ? (
        <Button
          {...sharedReplaceLinkButtonProps}
          onClick={() => {
            onLaunchReplaceFlow(usedLinkTypes[0]);
          }}
          dataTestAttrs={{component: 'ReplaceBiointelligentLinksInfoBanner', element: 'replaceButton'}}
        />
      ) : (
        <Dropdown
          menu={
            <Menu width={MenuWidth.FIT_CONTENT}>
              {usedLinkTypes.map((linkType: ReplaceableBiointelligentLinkType) => (
                <Option
                  key={linkType}
                  value={linkType}
                  onSelect={() => {
                    onLaunchReplaceFlow(linkType);
                  }}
                >
                  {capitalize(LINK_TYPE_TO_COMPONENT_NAME[linkType])} links
                </Option>
              ))}
            </Menu>
          }
        >
          <Button {...sharedReplaceLinkButtonProps} isDropdown={true} />
        </Dropdown>
      )}
    </div>
  );

  return (
    <InlineBanner
      bannerType={InlineBannerType.INFO}
      text={message}
      dataTestAttrs={{component: 'ReplaceBiointelligentLinksInfoBanner', element: 'root'}}
    />
  );
}

const styles = StyleSheet.create({
  messageWrapper: {
    display: 'flex',
  },
  infoText: {
    flex: 1,
  },
  replaceButton: {
    margin: 'auto',
  },
});
